import { formatNumber } from "@memorang/helpers/common";
import type React from "react";
import { useTranslation } from "react-i18next";
import { resolveTagIcon } from "../../constants";
import type { LevelIconName } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { NestedTagRow } from "../TagFiltersList";
import { useTagFilterSections } from "../TagFiltersList/useTagFilterSections";
import TooltipWrapper from "../TooltipWrapper";
import FilterListItem from "./FilterListItem";
import ListAccordion from "./ListAccordion";
import type { SectionTag } from "./types";

interface Props {
	selectedCheckboxMap?: Record<string, boolean>;
	sectionTags: SectionTag[];
	onSelectItem: (tagId: string, locked?: boolean) => void;
	maxFilterDepth: number;
	disableAll?: boolean;
	hasAccess?: boolean;
	showPremiumChip?: boolean;
	showFreeChip?: boolean;
	isDiagnostic?: boolean;
	disabledTagIds?: Set<number>;
	iconMap?: Record<string, LevelIconName>;
	disabledTooltip?: string;
	disabledTooltipMap?: Record<number, string>;
	disabledTooltipCursor?: React.CSSProperties["cursor"];
	itemVariant?: "checkbox" | "radio";
	hideItemCounts?: boolean;
	hideSectionHeaders?: boolean;
}

const FilterSections = ({
	selectedCheckboxMap,
	onSelectItem,
	sectionTags,
	maxFilterDepth,
	disableAll,
	hasAccess,
	showPremiumChip,
	showFreeChip,
	isDiagnostic,
	disabledTagIds,
	iconMap,
	disabledTooltip,
	disabledTooltipMap,
	disabledTooltipCursor,
	itemVariant,
	hideItemCounts,
	hideSectionHeaders,
}: Props) => {
	const { t } = useTranslation();
	const theme = useAppTheme();

	const { visibleSections, expandedMap, setExpanded } = useTagFilterSections({
		sectionTags,
		selectedCheckboxMap,
		maxFilterDepth,
		currentView: "Filter",
	});

	const accordions = visibleSections.map(({ title, rows }) => ({
		title,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		children: rows.map((row) => {
			const isFlat = row.level === 0 && !row.hasChildren;
			if (isFlat) {
				const numItems = row.tag.numItems ?? row.tag.items?.length ?? 0;
				const locked = isDiagnostic
					? false
					: Boolean(row.tag.locked) && hasAccess !== true;
				const tagDisabled = disabledTagIds?.has(row.tag.id) ?? false;
				const rowDisabled = numItems === 0 || tagDisabled;
				const checked = !!selectedCheckboxMap?.[row.tag.id] && !rowDisabled;
				const chipBg = checked
					? theme.colors.primary
					: theme.colors.surfaceVariant;
				const chipTextColor = checked
					? theme.colors.onPrimary
					: theme.colors.onSurfaceVariant;
				const icon = resolveTagIcon(row.tag, iconMap ?? {});
				const item = (
					<FilterListItem
						key={row.tag.id}
						title={row.tag.title}
						rightText={
							hideItemCounts
								? undefined
								: `${formatNumber(numItems)} ${t("common.item", { count: numItems })}`
						}
						status={checked ? "checked" : "unchecked"}
						locked={locked}
						color={hideItemCounts ? undefined : chipBg}
						textColor={hideItemCounts ? undefined : chipTextColor}
						icon={icon}
						variant={itemVariant}
						handleSelectCheckbox={() =>
							tagDisabled ? undefined : onSelectItem(String(row.tag.id), locked)
						}
						disabled={rowDisabled}
						disableAll={disableAll}
					/>
				);
				const tagTooltip = disabledTooltipMap?.[row.tag.id] ?? disabledTooltip;
				if (rowDisabled && tagTooltip) {
					return (
						<TooltipWrapper
							key={row.tag.id}
							title={tagTooltip}
							display="block"
							style={{ cursor: disabledTooltipCursor ?? "not-allowed" }}
						>
							{item}
						</TooltipWrapper>
					);
				}
				return item;
			}
			return (
				<NestedTagRow
					key={row.tag.id}
					tag={row.tag}
					level={row.level}
					checked={!!selectedCheckboxMap?.[row.tag.id]}
					expanded={!!expandedMap[row.tag.id]}
					hasCheckedDescendant={row.hasCheckedDescendant}
					hasChildren={row.hasChildren}
					disableAll={disableAll}
					isDiagnostic={isDiagnostic}
					hasAccess={hasAccess}
					maxFilterDepth={maxFilterDepth}
					handleSelectCheckbox={onSelectItem}
					setExpanded={setExpanded}
				/>
			);
		}),
	}));

	const premiumChipVisible =
		!!showPremiumChip &&
		!hasAccess &&
		sectionTags.every((item) => item.tags.every((tag) => tag.locked));

	return (
		<ListAccordion
			accordions={accordions}
			disabled={disableAll}
			showPremiumChip={premiumChipVisible}
			showFreeChip={showFreeChip}
			hideSectionHeaders={hideSectionHeaders}
		/>
	);
};

export default FilterSections;
